import type {
  ProductDetailResponse,
  ProductSummary,
} from "@cabal/shared/domain/session/product/types.js";
import type {
  VersionDetailResponse,
  VersionSummary,
} from "@cabal/shared/domain/session/version/types.js";
import {
  QUERY_STATUS,
  type QueryOptions,
  type QueryStatus,
} from "@/types/async-protocol";
import {
  MAIN_CHAT_PRODUCT_KEY,
  TRACKED_PRODUCT_FIELD,
  type ProductReadMarker,
  type ProductReadMarkerMap,
  type ProductUnreadCollabCounts,
  type ProductsSeenMap,
  type TrackedProductField,
} from "@/types/frontend-types";

export interface ProductChangesValue {
  changedFields: Set<TrackedProductField> | undefined;
  hasAnyChanges: boolean;
  hasChanged: (field: TrackedProductField) => boolean;
  nameChanged: boolean;
  typeChanged: boolean;
  roleChanged: boolean;
  statusChanged: boolean;
  ownerChanged: boolean;
  collabCountChanged: boolean;
  versionCountChanged: boolean;
  subproductCountChanged: boolean;
  latestVersionChanged: boolean;
  versionIdsChanged: boolean;
  attachedFilesChanged: boolean;
  descriptionChanged: boolean;
  definitionOfDoneChanged: boolean;
  summaryChanged: boolean;
}

interface SelectProductChangesValueInput {
  fields: Set<string> | undefined;
  isFlashing: boolean;
}

export function selectProductChangesValue({
  fields,
  isFlashing,
}: SelectProductChangesValueInput): ProductChangesValue {
  const changes = fields as Set<TrackedProductField> | undefined;
  const hasChanged = (field: TrackedProductField): boolean =>
    hasTrackedProductChange({ changes, field });

  return {
    changedFields: changes,
    hasAnyChanges: isFlashing,
    hasChanged,
    nameChanged: hasChanged(TRACKED_PRODUCT_FIELD.Name),
    typeChanged: hasChanged(TRACKED_PRODUCT_FIELD.Type),
    roleChanged: hasChanged(TRACKED_PRODUCT_FIELD.Role),
    statusChanged: hasChanged(TRACKED_PRODUCT_FIELD.Status),
    ownerChanged: hasChanged(TRACKED_PRODUCT_FIELD.Owner),
    collabCountChanged: hasChanged(TRACKED_PRODUCT_FIELD.CollabCount),
    versionCountChanged: hasChanged(TRACKED_PRODUCT_FIELD.VersionCount),
    subproductCountChanged: hasChanged(TRACKED_PRODUCT_FIELD.SubproductCount),
    latestVersionChanged: hasChanged(TRACKED_PRODUCT_FIELD.LatestVersionNumber),
    versionIdsChanged: hasChanged(TRACKED_PRODUCT_FIELD.VersionIds),
    attachedFilesChanged: hasChanged(TRACKED_PRODUCT_FIELD.AttachedFiles),
    descriptionChanged: hasChanged(TRACKED_PRODUCT_FIELD.Description),
    definitionOfDoneChanged: hasChanged(TRACKED_PRODUCT_FIELD.DefinitionOfDone),
    summaryChanged: hasChanged(TRACKED_PRODUCT_FIELD.Summary),
  };
}

interface HasTrackedProductChangeInput {
  changes: Set<TrackedProductField> | undefined;
  field: TrackedProductField;
}

function hasTrackedProductChange({
  changes,
  field,
}: HasTrackedProductChangeInput): boolean {
  return changes?.has(field) ?? false;
}

interface SelectSessionDataQueryOptionsInput {
  isReady: boolean;
  options: QueryOptions | undefined;
}

export function selectSessionDataQueryOptions({
  isReady,
  options,
}: SelectSessionDataQueryOptionsInput): QueryOptions {
  return {
    enabled: selectQueryEnabled({ isReady, options }),
    ...selectQueryTimingOptions(options),
  };
}

function selectQueryEnabled({
  isReady,
  options,
}: SelectSessionDataQueryOptionsInput): boolean {
  return isReady && (options?.enabled ?? true);
}

function selectQueryTimingOptions(
  options: QueryOptions | undefined,
): Pick<QueryOptions, "refetchInterval" | "refetchOnFocus"> {
  return {
    refetchInterval: options?.refetchInterval,
    refetchOnFocus: options?.refetchOnFocus,
  };
}

export interface ProductReadStateValue {
  scopeKey: string;
  isMainChat: boolean;
  marker: ProductReadMarker;
  unread: { totalCollabs: number; unreadCollabs: number };
  productSeen: {
    lastChangedProduct: Date | null;
    lastAddedCollabs: Date | null;
    lastAddedVersion: Date | null;
  } | null;
}

interface SelectProductReadStateValueInput {
  productId: string | null;
  mainChatScopeKey: string;
  productReads: ProductReadMarkerMap;
  unreadCollabCounts: ProductUnreadCollabCounts;
  productsSeen: ProductsSeenMap;
}

const EMPTY_PRODUCT_READ_MARKER: ProductReadMarker = {
  productSeenAt: null,
  collabsReadAt: null,
};

const EMPTY_PRODUCT_UNREAD_COLLAB_COUNTS = {
  totalCollabs: 0,
  unreadCollabs: 0,
};

export function selectProductReadStateValue({
  productId,
  mainChatScopeKey,
  productReads,
  unreadCollabCounts,
  productsSeen,
}: SelectProductReadStateValueInput): ProductReadStateValue {
  const scopeKey = productId ?? mainChatScopeKey;

  return {
    scopeKey,
    isMainChat: scopeKey === MAIN_CHAT_PRODUCT_KEY,
    marker: productReads[scopeKey] ?? EMPTY_PRODUCT_READ_MARKER,
    unread: unreadCollabCounts[scopeKey] ?? EMPTY_PRODUCT_UNREAD_COLLAB_COUNTS,
    productSeen: selectProductSeen({ productId, productsSeen }),
  };
}

function selectProductSeen(input: {
  productId: string | null;
  productsSeen: ProductsSeenMap;
}): ProductReadStateValue["productSeen"] {
  if (!input.productId) {
    return null;
  }

  return input.productsSeen[input.productId] ?? null;
}

interface SelectSessionVersionsInput {
  product: ProductSummary | undefined;
  versionsById: Record<string, VersionSummary>;
}

interface SessionVersionsSelection {
  versions: VersionSummary[];
  hasCompleteContextVersions: boolean;
}

export function selectSessionVersions({
  product,
  versionsById,
}: SelectSessionVersionsInput): SessionVersionsSelection {
  if (!product) {
    return {
      versions: [],
      hasCompleteContextVersions: false,
    };
  }

  const knownVersionIds = product.versionIds;
  const versions = knownVersionIds
    .map((versionId) => versionsById[versionId])
    .filter((version): version is VersionSummary => Boolean(version));

  return {
    versions,
    // A partial cache must stay loading/errored; treating one cached row as a
    // complete list can render the wrong active version during transitions.
    hasCompleteContextVersions: versions.length === knownVersionIds.length,
  };
}

interface SelectSessionVersionsSignatureInput {
  productId: string | null | undefined;
  product: ProductSummary | undefined;
  productReads: ProductReadMarkerMap;
}

export function selectSessionVersionsSignature({
  productId,
  product,
  productReads,
}: SelectSessionVersionsSignatureInput): string {
  if (!productId) return "";

  // Keep version arrivals and per-product read markers in the query key.
  const collabsReadTime = productReads[productId]?.collabsReadAt?.getTime() ?? 0;
  return `${product?.versionIds.join("|") ?? ""}|${collabsReadTime}`;
}

interface SelectSessionVersionsResultInput<TError> {
  queryData: VersionSummary[] | null;
  queryStatus: QueryStatus;
  queryError: TError | null;
  versionSelection: SessionVersionsSelection;
}

interface SessionVersionsResult<TError> {
  versions: VersionSummary[];
  queryState: HydratedQueryState<TError>;
}

export function selectSessionVersionsResult<TError>({
  queryData,
  queryStatus,
  queryError,
  versionSelection,
}: SelectSessionVersionsResultInput<TError>): SessionVersionsResult<TError> {
  const versions = queryData ?? (
    versionSelection.hasCompleteContextVersions ? versionSelection.versions : []
  );
  const hasVersionsData =
    queryData != null || versionSelection.hasCompleteContextVersions;

  return {
    versions,
    queryState: selectHydratedQueryState({
      hasData: hasVersionsData,
      queryStatus,
      queryError,
    }),
  };
}

interface SelectProductDetailInput {
  productId: string | null | undefined;
  cachedDetailsById: Record<string, ProductDetailResponse>;
  fetchedDetail: ProductDetailResponse | null;
}

interface ProductDetailSelection {
  fetchedProductDetail: ProductDetailResponse | null;
  detailData: ProductDetailResponse | null;
  hasProductDetail: boolean;
}

export function selectProductDetail({
  productId,
  cachedDetailsById,
  fetchedDetail,
}: SelectProductDetailInput): ProductDetailSelection {
  const cachedProductDetail = selectCachedProductDetail({
    productId,
    cachedDetailsById,
  });
  const fetchedProductDetail = selectFetchedProductDetail({
    productId,
    fetchedDetail,
  });
  const detailData = fetchedProductDetail ?? cachedProductDetail;

  return {
    fetchedProductDetail,
    detailData,
    hasProductDetail: detailData != null,
  };
}

interface ProductDetailObservation {
  productId: string;
  description: string | null;
  definitionOfDone: string | null;
  summary: string | null;
}

interface SelectProductDetailObservationInput {
  detail: ProductDetailResponse | null;
}

export function selectProductDetailObservation({
  detail,
}: SelectProductDetailObservationInput): ProductDetailObservation | null {
  if (!hasProductDetailId(detail)) return null;

  return {
    productId: detail.id,
    description: toNullableText(detail.description),
    definitionOfDone: toNullableText(detail.definitionOfDone),
    summary: toNullableText(detail.summary),
  };
}

function hasProductDetailId(
  detail: ProductDetailResponse | null,
): detail is ProductDetailResponse & { id: string } {
  return Boolean(detail?.id);
}

function toNullableText(value: string | null | undefined): string | null {
  return value ?? null;
}

interface SelectCachedProductDetailInput {
  productId: string | null | undefined;
  cachedDetailsById: Record<string, ProductDetailResponse>;
}

function selectCachedProductDetail({
  productId,
  cachedDetailsById,
}: SelectCachedProductDetailInput): ProductDetailResponse | null {
  if (!productId) return null;
  return cachedDetailsById[productId] ?? null;
}

interface SelectFetchedProductDetailInput {
  productId: string | null | undefined;
  fetchedDetail: ProductDetailResponse | null;
}

function selectFetchedProductDetail({
  productId,
  fetchedDetail,
}: SelectFetchedProductDetailInput): ProductDetailResponse | null {
  if (!fetchedDetail || fetchedDetail.id !== productId) return null;
  return fetchedDetail;
}

interface SelectVersionProductIdInput {
  versionId: string | null | undefined;
  productsById: Record<string, ProductSummary>;
  cachedVersionDetail: VersionDetailResponse | null;
}

export function selectVersionProductId({
  versionId,
  productsById,
  cachedVersionDetail,
}: SelectVersionProductIdInput): string | null {
  if (!versionId) return null;

  return selectProductIdFromVersionList({ versionId, productsById }) ??
    selectCachedVersionProductId(cachedVersionDetail);
}

function selectProductIdFromVersionList(input: {
  versionId: string;
  productsById: Record<string, ProductSummary>;
}): string | null {
  return Object.values(input.productsById).find((product) =>
    product.versionIds.includes(input.versionId),
  )?.id ?? null;
}

function selectCachedVersionProductId(
  cachedVersionDetail: VersionDetailResponse | null,
): string | null {
  return cachedVersionDetail?.productId ?? null;
}

interface SelectVersionDetailInput {
  versionId: string | null | undefined;
  cachedDetailsById: Record<string, VersionDetailResponse>;
  fetchedDetail: VersionDetailResponse | null;
}

interface VersionDetailSelection {
  fetchedVersionDetail: VersionDetailResponse | null;
  detailData: VersionDetailResponse | null;
  hasVersionDetail: boolean;
}

export function selectVersionDetail({
  versionId,
  cachedDetailsById,
  fetchedDetail,
}: SelectVersionDetailInput): VersionDetailSelection {
  const cachedVersionDetail = selectCachedVersionDetail({
    versionId,
    cachedDetailsById,
  });
  const fetchedVersionDetail = selectFetchedVersionDetail({
    versionId,
    fetchedDetail,
  });
  const detailData = fetchedVersionDetail ?? cachedVersionDetail;

  return {
    fetchedVersionDetail,
    detailData,
    hasVersionDetail: detailData != null,
  };
}

interface SelectCachedVersionDetailInput {
  versionId: string | null | undefined;
  cachedDetailsById: Record<string, VersionDetailResponse>;
}

function selectCachedVersionDetail({
  versionId,
  cachedDetailsById,
}: SelectCachedVersionDetailInput): VersionDetailResponse | null {
  if (!versionId) return null;
  return cachedDetailsById[versionId] ?? null;
}

interface SelectFetchedVersionDetailInput {
  versionId: string | null | undefined;
  fetchedDetail: VersionDetailResponse | null;
}

function selectFetchedVersionDetail({
  versionId,
  fetchedDetail,
}: SelectFetchedVersionDetailInput): VersionDetailResponse | null {
  if (!fetchedDetail || fetchedDetail.id !== versionId) return null;
  return fetchedDetail;
}

interface VersionReflectionObservation {
  versionId: string;
  productId: string;
  reflectionNotes: readonly {
    id: string;
    note: string;
    authorMember: string | null;
  }[];
}

interface SelectVersionReflectionObservationInput {
  detail: VersionDetailResponse | null;
}

export function selectVersionReflectionObservation({
  detail,
}: SelectVersionReflectionObservationInput): VersionReflectionObservation | null {
  if (!detail?.id || !detail.productId) return null;

  return {
    versionId: detail.id,
    productId: detail.productId,
    reflectionNotes: detail.reflectionNotes.map((note) => ({
      id: note.id,
      note: note.note,
      authorMember: note.authorMember,
    })),
  };
}

interface SelectHydratedQueryStateInput<TError> {
  hasData: boolean;
  queryStatus: QueryStatus;
  queryError: TError | null;
}

interface HydratedQueryState<TError> {
  status: QueryStatus;
  error: TError | null;
  isLoading: boolean;
}

export function selectHydratedQueryState<TError>({
  hasData,
  queryStatus,
  queryError,
}: SelectHydratedQueryStateInput<TError>): HydratedQueryState<TError> {
  if (hasData) {
    return {
      status: QUERY_STATUS.Success,
      error: null,
      isLoading: false,
    };
  }

  return {
    status: queryStatus,
    error: queryError,
    isLoading: queryStatus === QUERY_STATUS.Loading,
  };
}
